import { Controller } from '@hotwired/stimulus';
import { isDesktop, isProduction, isWLA } from '../../utils/dev';
/*
 * Description
 * -------
 *
 * Creates emoji picker using emoji-mart (only on desktop). Dispatches selectEmoji event when an emoji is selected.
 *
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * - button - (required) - element that toggles emoji picker
 * - container - (required) - element containing emoji picker
 *
 * Values
 * -------
 *
 * - custom-emojis - (optional) - custom emojis to be displayed in the emoji picker
 *
 * Usage
 * -------
 *
 * <div data-controller="emoji-picker">
 *   <div data-emoji-picker-target="container"></div>
 *   <button class="hidden" data-emoji-picker-target="button" data-action="click->emoji-picker#toggle">
 * </div>
 */
export class EmojiPicker extends Controller {
    static targets = ['button', 'container'];
    static values = { customEmojis: String };
    async connect() {
        if (isProduction()) {
            if (!isDesktop())
                return;
            if (isWLA())
                return;
        }
        else {
            if (isWLA())
                return;
            if (!isDesktop() && this.customEmojis.length === 0)
                return;
        }
        const { createPopper } = await import('@popperjs/core');
        const { Picker } = await import('emoji-mart');
        const { default: data } = await import('@emoji-mart/data');
        this.buttonTarget.classList.remove('hidden');
        const emojiPicker = new Picker({
            data,
            theme: 'light',
            skinTonePosition: 'none',
            onClickOutside: (e) => {
                if (e.target !== this.buttonTarget) {
                    this.hide();
                }
            },
            custom: this.getCustomEmojis(),
            categories: [
                'frequent',
                'custom',
                'people',
                'nature',
                'foods',
                'activity',
                'places',
                'objects',
                'symbols',
                'flags',
            ],
            previewPosition: 'none',
            onEmojiSelect: (e) => {
                this.hide();
                this.dispatch('selectEmoji', { detail: e });
            },
        });
        this.containerTarget.appendChild(emojiPicker);
        this.popperInstance = createPopper(this.buttonTarget, this.containerTarget, {
            placement: 'top',
            modifiers: [
                {
                    name: 'computeStyles',
                    options: {
                        gpuAcceleration: false,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: [0, 16],
                    },
                },
            ],
        });
    }
    hide() {
        this.containerTarget.classList.add('hidden');
        this.popperInstance?.update();
        this.isEmojiPickerOpen = false;
    }
    show() {
        this.containerTarget.classList.remove('hidden');
        this.isEmojiPickerOpen = true;
        this.popperInstance?.update();
    }
    toggle() {
        if (this.isEmojiPickerOpen) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    getCustomEmojis() {
        if (this.customEmojis.length === 0)
            return undefined;
        return [
            {
                id: 'custom',
                name: 'For you',
                emojis: this.customEmojis.map((emoji) => ({
                    id: emoji.name,
                    name: emoji.name,
                    keywords: [emoji.name],
                    skins: [{ src: emoji.image_url }],
                })),
            },
        ];
    }
    disconnect() {
        this.popperInstance?.destroy();
    }
    get customEmojis() {
        return JSON.parse(this.customEmojisValue);
    }
}
